import { useCallback, useEffect, useState } from "react";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import { AiCreditUsage } from "../AiCreditUsage";

/**
 * Fetches the current monthly AI credit usage for the active org on mount and
 * whenever the user switches orgs in the org switcher.
 *
 * On an org switch, the previous org's `usage` and `error` are cleared before
 * the new request fires so consumers never render another org's numbers under
 * the new org's header. An `AbortController` tied to the effect aborts an
 * in-flight request when the org changes again or the consumer unmounts, so
 * a late-arriving response from the prior org cannot overwrite fresher state.
 *
 * No polling: usage is fetched once per org switch and not refreshed while
 * the consumer stays mounted on the same org.
 *
 * If no org is active (mid-switch, signed-out flicker), the hook stays in its
 * initial loading state without setting an error. The effect re-fires when an
 * org becomes available and the usual fetch/abort lifecycle resumes.
 */
export function useAiCreditUsage() {
  const { currentOrganization } = useAuth();
  const { limitSummary } = useDomainServices();
  // Narrow the dependency to the org id so an unstable `currentOrganization`
  // object reference (common with React context) doesn't rebuild `getUsage`
  // and re-fire the effect on every parent render.
  const orgId = currentOrganization?.org_id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [usage, setUsage] = useState<AiCreditUsage | null>(null);

  const getUsage = useCallback(
    async (signal: AbortSignal) => {
      try {
        const result = await limitSummary.getAiCreditUsageForOrg(orgId!, {
          abortSignal: signal,
        });

        setUsage(result);
        setError(null);
      } catch (error) {
        // Identify a deliberate cancel by the thrown AbortError rather than
        // by `signal.aborted`: a race in which `abort()` runs *after* an
        // unrelated error was thrown would otherwise let the abort flag
        // swallow that real failure.
        if (error instanceof Error) {
          if (error.name === "AbortError") {
            return;
          }
          setError(error);
        } else {
          // Wrap non-Error throwables so consumers always see an Error;
          // preserve the original via `cause` for debugging.
          setError(new Error(String(error), { cause: error }));
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    },
    [orgId, limitSummary],
  );

  useEffect(() => {
    // No active org: stay in the initial loading state without setting an
    // error. "Org not set" is a transient mid-switch / signed-out state, not
    // a fetch failure — surfacing it through `error` would muddle the two.
    if (!orgId) {
      return;
    }

    // Clear the previous org's data so consumers don't render its numbers
    // under the new org while the next request is in flight.
    setLoading(true);
    setError(null);
    setUsage(null);
    const abortController = new AbortController();
    void getUsage(abortController.signal);
    return () => abortController.abort();
  }, [orgId, getUsage]);

  return {
    usage,
    loading,
    error,
  };
}
